import { Button, Group } from '@mantine/core';
import { modals } from '@mantine/modals';
import { AiOutlinePlus, AiOutlineDelete, AiOutlineReload, AiOutlineClear } from 'react-icons/ai';
import { useState } from 'react';
import { Price } from '../types';
import { 
    createStripePrice, 
    updateStripePrice, 
    deleteStripePrice 
} from '../../stripe-api-integration/services/StripePriceService';
import { update } from '../services/PricesService';
import { fetchRecords } from '../services/PricesService';
import { fetch as fetchProduct } from '../../products/services/ProductsService';

interface StripePriceButtonsProps {
    price: Price;
    onPriceUpdated?: (stripePriceId: string) => void;
}

export const StripePriceButtons = ({ price, onPriceUpdated }: StripePriceButtonsProps) => {
    const [loading, setLoading] = useState(false);
    
    const hasStripePrice = !!price.stripePriceId;
    const [stripeProductId, setStripeProductId] = useState<string | null>(null);
    
    // Fetch product data if not available
    useState(() => {
        if (!price.product?.billingProviderRecordId && price.productId) {
            fetchProduct(price.productId).then(product => {
                setStripeProductId(product?.billingProviderRecordId || null);
            });
        } else {
            setStripeProductId(price.product?.billingProviderRecordId || null);
        }
    });
    
    const handleCreateStripePrice = async () => {
        if (!stripeProductId) {
            console.error('Product must have a Stripe product ID');
            return;
        }
        
        setLoading(true);
        let stripePriceId: string | null = null;
        
        try {
            // Check for existing local prices with Stripe price ID and same amount/description
            const existingPrices = await fetchRecords(price.productId, {});
            const duplicatePrice = existingPrices?.items?.find(p => 
                p.stripePriceId && 
                p.amount === price.amount && 
                p.description === price.description &&
                p.id !== price.id
            );
            
            if (duplicatePrice?.stripePriceId) {
                // Use existing Stripe price ID
                stripePriceId = duplicatePrice.stripePriceId;
            } else {
                const stripePrice = await createStripePrice({
                    currency: 'usd',
                    product: stripeProductId,
                    unitAmount: price.amount,
                    nickname: price.description,
                    recurring: price.recurringOption === 'Yes' ? {
                        interval: price.billingPeriod.toLowerCase() === 'monthly' ? 'month' : 
                                 price.billingPeriod.toLowerCase() === 'annually' ? 'year' :
                                 price.billingPeriod.toLowerCase() === 'weekly' ? 'week' :
                                 price.billingPeriod.toLowerCase() === 'daily' ? 'day' : 'month',
                        intervalCount: 1
                    } : undefined
                });
                
                stripePriceId = stripePrice?.id || null;
            }
            
            if (stripePriceId) {
                try {
                    await update(price.id, {
                        id: price.id,
                        productId: price.productId,
                        priceStatus: price.priceStatus,
                        pricingModel: price.pricingModel,
                        recurringOption: price.recurringOption,
                        amount: price.amount,
                        ratePlanId: price.ratePlanId,
                        description: price.description,
                        billingPeriod: price.billingPeriod,
                        stripePriceId
                    });
                    if (onPriceUpdated) {
                        onPriceUpdated(stripePriceId);
                    }
                } catch (updateError) {
                    // Rollback: Delete the Stripe price if local update fails
                    try {
                        await deleteStripePrice(stripePriceId);
                    } catch (rollbackError) {
                        console.error('Failed to rollback Stripe price creation:', rollbackError);
                    }
                    throw updateError;
                }
            }
        } catch (error) {
            console.error('Failed to create Stripe price:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStripePrice = async () => {
        if (!price.stripePriceId) return;
        
        setLoading(true);
        try {
            await updateStripePrice(price.stripePriceId, {
                nickname: price.description,
                active: price.priceStatus === 'Active'
            });
        } catch (error) {
            console.error('Failed to update Stripe price:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteStripePrice = async () => {
        if (!price.stripePriceId) return;
        
        setLoading(true);
        
        try {
            // Archive the price in Stripe (Stripe doesn't actually delete prices)
            await deleteStripePrice(price.stripePriceId);
            
            // Clear stripePriceId since price is archived in Stripe
            await update(price.id, {
                id: price.id,
                productId: price.productId,
                priceStatus: price.priceStatus,
                pricingModel: price.pricingModel,
                recurringOption: price.recurringOption,
                amount: price.amount,
                ratePlanId: price.ratePlanId,
                description: price.description,
                billingPeriod: price.billingPeriod,
                stripePriceId: undefined // Clear the Stripe price ID
            });
            
            if (onPriceUpdated) {
                onPriceUpdated('');
            }
        } catch (error) {
            console.error('Failed to archive Stripe price:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClearStripeId = () => {
        modals.openConfirmModal({
            title: 'Clear Stripe Price ID',
            children: 'This will remove the Stripe price ID from your local record without affecting Stripe. Are you sure?',
            labels: { confirm: 'Clear ID', cancel: 'Cancel' },
            confirmProps: { color: 'orange' },
            onConfirm: () => clearStripeId()
        });
    };

    const clearStripeId = async () => {
        if (!price.stripePriceId) return;
        
        setLoading(true);
        
        try {
            await update(price.id, {
                id: price.id,
                productId: price.productId,
                priceStatus: price.priceStatus,
                pricingModel: price.pricingModel,
                recurringOption: price.recurringOption,
                amount: price.amount,
                ratePlanId: price.ratePlanId,
                description: price.description,
                billingPeriod: price.billingPeriod,
                stripePriceId: undefined
            });
            
            if (onPriceUpdated) {
                onPriceUpdated('');
            }
        } catch (error) {
            console.error('Failed to clear Stripe price ID:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Group gap="xs">
            {!hasStripePrice ? (
                <Button
                    leftSection={<AiOutlinePlus size={16} />}
                    size="xs"
                    variant="light"
                    loading={loading}
                    onClick={handleCreateStripePrice}
                    disabled={!stripeProductId}
                >
                    Create in Stripe
                </Button>
            ) : (
                <>
                    <Button
                        leftSection={<AiOutlineReload size={16} />}
                        size="xs"
                        variant="light"
                        loading={loading}
                        onClick={handleUpdateStripePrice}
                    >
                        Update Stripe
                    </Button>
                    <Button
                        leftSection={<AiOutlineDelete size={16} />}
                        size="xs"
                        variant="light"
                        color="red"
                        loading={loading}
                        onClick={handleDeleteStripePrice}
                    >
                        Archive in Stripe
                    </Button>
                    <Button
                        leftSection={<AiOutlineClear size={16} />}
                        size="xs"
                        variant="light"
                        color="orange"
                        loading={loading}
                        onClick={handleClearStripeId}
                    >
                        Clear Stripe ID
                    </Button>
                </>
            )}
        </Group>
    );
};